import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Bell,
  BellRing,
  Check,
  ChevronLeft,
  Globe,
  Mail,
  MessageSquare,
  Pencil,
  Plus,
  Search,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { notificationsApi } from '@/api/endpoints/notifications';
import { ApiError } from '@/api/client';
import { translateEvent } from '@/lib/traccarLabels';
import { cn } from '@/lib/utils';
import type { Notification, NotificationType } from '@/api/types';

export function NotificationRulesPage() {
  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list({ all: true }),
  });
  const types = useQuery({
    queryKey: ['notification-types'],
    queryFn: () => notificationsApi.types(),
  });
  const [editing, setEditing] = useState<Notification | 'new' | null>(null);
  const [search, setSearch] = useState('');
  const [testFeedback, setTestFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = list.data ?? [];
    if (!q) return data;
    return data.filter(
      (n) =>
        translateEvent(n.type).toLowerCase().includes(q) || n.type.toLowerCase().includes(q),
    );
  }, [list.data, search]);

  const testMutation = useMutation({
    mutationFn: () => notificationsApi.test(),
    onSuccess: () => setTestFeedback({ ok: true, msg: 'Notificação de teste enviada.' }),
    onError: (e) => {
      const msg = e instanceof ApiError ? `${e.status} ${e.statusText}` : (e as Error).message;
      setTestFeedback({ ok: false, msg });
    },
  });

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Link
        to="/notifications"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar para notificações
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Regras de notificação</h1>
          <p className="text-sm text-muted-foreground">
            Configurações de evento (web, e-mail, SMS). As notificações criadas aqui (ou no
            painel Trackmax) viram eventos no feed.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending}
            className="gap-1.5"
          >
            <Send className="h-4 w-4" />
            {testMutation.isPending ? 'Enviando…' : 'Testar envio'}
          </Button>
          <Button onClick={() => setEditing('new')} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nova notificação
          </Button>
        </div>
      </div>

      {testFeedback && (
        <div
          className={cn(
            'flex items-start gap-2 rounded-md px-3 py-2 text-xs font-medium',
            testFeedback.ok
              ? 'bg-status-running/10 text-status-running'
              : 'bg-status-blocked/10 text-status-blocked',
          )}
        >
          {testFeedback.ok ? (
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          ) : (
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          )}
          {testFeedback.msg}
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por tipo de evento"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {list.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando notificações…</p>
      ) : list.error ? (
        <p className="p-6 text-sm text-destructive">Erro ao carregar notificações.</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Bell className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {search
                ? 'Nenhuma notificação corresponde à busca.'
                : 'Nenhuma regra cadastrada.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((n) => (
            <NotificationCard
              key={n.id}
              notification={n}
              onEdit={() => setEditing(n)}
            />
          ))}
        </div>
      )}

      {editing && (
        <NotificationEditor
          initial={editing === 'new' ? null : editing}
          types={types.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function NotificationCard({
  notification,
  onEdit,
}: {
  notification: Notification;
  onEdit: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <BellRing className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{translateEvent(notification.type)}</p>
              <p className="truncate font-mono text-[10px] text-muted-foreground">
                {notification.type}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 text-[11px]">
          {notification.web && (
            <Badge icon={Globe} label="Web" color="text-primary" bg="bg-primary/10" />
          )}
          {notification.mail && (
            <Badge icon={Mail} label="E-mail" color="text-status-ready" bg="bg-status-ready/10" />
          )}
          {notification.sms && (
            <Badge
              icon={MessageSquare}
              label="SMS"
              color="text-status-running"
              bg="bg-status-running/10"
            />
          )}
          {notification.always && (
            <Badge icon={Bell} label="Sempre" color="text-status-off" bg="bg-status-off/15" />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Badge({
  icon: Icon,
  label,
  color,
  bg,
}: {
  icon: typeof Globe;
  label: string;
  color: string;
  bg: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold',
        bg,
        color,
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

interface FormState {
  type: string;
  always: boolean;
  web: boolean;
  mail: boolean;
  sms: boolean;
}

function fromNotification(n: Notification): FormState {
  return {
    type: n.type ?? '',
    always: n.always ?? false,
    web: n.web ?? false,
    mail: n.mail ?? false,
    sms: n.sms ?? false,
  };
}

function NotificationEditor({
  initial,
  types,
  onClose,
}: {
  initial: Notification | null;
  types: NotificationType[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(
    initial
      ? fromNotification(initial)
      : { type: types[0]?.type ?? 'deviceOnline', always: false, web: true, mail: false, sms: false },
  );
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (initial) setForm(fromNotification(initial));
  }, [initial]);

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.type) e.type = 'Obrigatório';
    if (!form.web && !form.mail && !form.sms) e.web = 'Escolha pelo menos 1 canal';
    return e;
  }, [form]);

  const valid = Object.keys(errors).length === 0;

  const buildPayload = (): Notification =>
    ({
      ...(initial ?? { id: 0, type: '' }),
      type: form.type,
      always: form.always,
      web: form.web,
      mail: form.mail,
      sms: form.sms,
    }) as Notification;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = buildPayload();
      if (initial) return notificationsApi.update(initial.id, payload);
      const { id, ...rest } = payload;
      void id;
      return notificationsApi.create(rest);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => notificationsApi.delete(initial!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
  });

  const errorText = (e: unknown) => {
    if (e instanceof ApiError) return `Servidor respondeu ${e.status} (${e.statusText || 'erro'}).`;
    if (e instanceof Error) return e.message;
    return 'Erro desconhecido.';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  const onConfirmDelete = () => {
    if (!initial) return;
    if (!window.confirm('Excluir esta regra de notificação?')) return;
    deleteMutation.mutate();
  };

  // Ordena tipos com nomes traduzidos primeiro
  const sortedTypes = useMemo(() => {
    return [...types].sort((a, b) =>
      translateEvent(a.type).localeCompare(translateEvent(b.type), 'pt-BR'),
    );
  }, [types]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-md flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">
            {initial ? 'Editar notificação' : 'Nova notificação'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 p-4">
          <Field label="Tipo de evento" required error={touched ? errors.type : undefined}>
            <select
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {sortedTypes.map((t) => (
                <option key={t.type} value={t.type}>
                  {translateEvent(t.type)} ({t.type})
                </option>
              ))}
            </select>
          </Field>

          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="text-xs font-semibold text-muted-foreground">Canais</p>
            <Toggle
              icon={Globe}
              label="Notificação no painel web"
              checked={form.web}
              onChange={(v) => setForm((f) => ({ ...f, web: v }))}
            />
            <Toggle
              icon={Mail}
              label="E-mail"
              checked={form.mail}
              onChange={(v) => setForm((f) => ({ ...f, mail: v }))}
            />
            <Toggle
              icon={MessageSquare}
              label="SMS"
              checked={form.sms}
              onChange={(v) => setForm((f) => ({ ...f, sms: v }))}
            />
            {touched && errors.web && (
              <p className="text-[11px] font-semibold text-status-blocked">{errors.web}</p>
            )}
          </div>

          <Toggle
            icon={Bell}
            label="Notificar sempre (mesmo fora dos horários do calendário)"
            checked={form.always}
            onChange={(v) => setForm((f) => ({ ...f, always: v }))}
          />

          {(saveMutation.error || deleteMutation.error) && (
            <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {errorText(saveMutation.error ?? deleteMutation.error)}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={onConfirmDelete}
              disabled={deleteMutation.isPending || saveMutation.isPending}
              className="mr-auto gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
            <Check className="h-4 w-4" />
            {saveMutation.isPending ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}

function Toggle({
  icon: Icon,
  label,
  checked,
  onChange,
}: {
  icon: typeof Bell;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
      />
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium">{label}</span>
    </label>
  );
}
